<template>
  <div class="w-full">
    <label for="users_emails" class="block text-sm font-medium text-gray-700">
      {{ getListLabel() }}
    </label>
    <div v-if="userType === 'participant'" class="text-xxs min-h-9 md:min-h-0 text-gray-500 mb-2">
      Join with video, voice and chat for live interaction.
    </div>
    <div v-if="userType === 'observer'" class="text-xxs min-h-9 md:min-h-0 text-gray-500 mb-2">
      Watch and interact through chat.
    </div>
    <div class="flex gap-x-2">
      <v-select
        :data-cy="`${userType}-invitation`"
        class="
          mt-1
          h-0
          block
          w-full
          border-gray-300
          focus:ring-vice-teal-500
          focus:border-vice-teal-500
          rounded-md
          shadow-sm
          sm:text-sm
          text-gray-900
        "
        id="users_emails"
        :placeholder="placeholder"
        ref="usersSelect"
        @search="onSearch"
        :disabled="!canAddUsers"
        label="email"
        :options="options"
        @option:selected="selectedOption"
        :create-option="onCreateOption"
        @keyup.enter="addMultipleUsers"
      >
        <template #option="option">
          <div
            v-if="option.isGroup"
            class="font-bold text-vice-teal-700 py-1"
            @mouseover="highlightedInGroup = option.name"
            @mouseleave="highlightedInGroup = null"
          >
            Group: {{ option.name }}
          </div>
          <div
            v-else
            :class="{
              'pl-4': option.groupName,
              'bg-vice-teal-150 rounded-md leading-5 border border-vice-teal-500':
                highlightedInGroup === option.groupName,
            }"
          >
            {{ option.email || option.name }}
          </div>
        </template>
        <template v-slot:no-options>
          <span v-if="searchMatchedWithSelected">This user has already been selected</span>
          <span v-else>
            We cannot find users within your organization with these emails. To add external
            participants, enter their full email address and click "Add"
          </span>
        </template>
        <template #open-indicator>
          <!-- Custom dropdown icon -->
          <span class="mr-1">
            <SelectDropdownArrow class="h-5 w-5 text-gray-400" aria-hidden="true" />
          </span>
        </template>
      </v-select>
      <div
        @click="addMultipleUsers"
        :class="{
          'opacity-50 cursor-not-allowed': !canAddUsers,
        }"
        class="
          rounded
          w-20
          cursor-pointer
          bg-vice-teal-400
          flex
          justify-center
          items-center
          text-sm text-white
          hover:bg-vice-teal-500
          add-button
        "
      >
        <!-- <PlusIcon class="w-5 pr-1" /> -->
        ADD
      </div>
    </div>
    <div class="my-4 border rounded-lg p-px shadow">
      <table class="table-fixed w-full divide-y divide-gray-200 rounded-md overflow-hidden">
        <thead class="bg-gray-100">
          <tr>
            <th
              scope="col"
              class="
                w-1/4
                md:w-1/3
                whitespace-nowrap
                md:p-4
                p-2
                text-left text-xxs
                font-medium
                text-gray-500
                tracking-wider
              "
            >
              Email
            </th>
            <th
              scope="col"
              class="
                w-1/4
                whitespace-nowrap
                md:p-4
                p-2
                text-left text-xxs
                font-medium
                text-gray-500
                tracking-wider
              "
            >
              Invite status
            </th>
            <th
              scope="col"
              class="
                w-1/5
                whitespace-nowrap
                md:p-4
                p-2
                text-right text-xxs
                font-medium
                text-gray-500
                tracking-wider
              "
            >
              Options
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(invitedUser, index) in selectedOptions"
            :key="invitedUser.email"
            :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
          >
            <td class="px-2 md:px-4 py-1 md:w-2/3 w-full text-left">
              <div class="font-medium text-gray-600 text-xxs md:text-xs break-words">
                {{ invitedUser.email }}
              </div>
            </td>
            <td class="px-0 md:px-4 py-1 w-1/4 md:whitespace-nowrap">
              <span class="flex gap-1 items-center" v-if="invitedUser.accepted_at">
                <ViceBadge green small>Already accepted</ViceBadge>
              </span>
              <span class="flex gap-1 items-center" v-else-if="!invitedUser.email_sent">
                <ViceBadge indigo small>Sending email</ViceBadge>
              </span>
              <span class="flex gap-1 items-center" v-else>
                <ViceBadge yellow small>Invitation sent</ViceBadge>
              </span>
            </td>

            <td class="px-4 py-1 w-1/4 whitespace-nowrap text-gray-500">
              <a
                href="javascript:void(0)"
                :data-cy="`remove-${userType}`"
                @click="removeInvite(invitedUser)"
              >
                <ViceButton small outline red icon="UserRemoveIcon">Remove</ViceButton>
              </a>
            </td>
          </tr>

          <tr v-if="!selectedOptions.length">
            <td colspan="3" class="p-4 w-2/3 whitespace-nowrap text-center">
              <span class="text-xs text-gray-500">No {{ userType }}s invited yet.</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Upload CSV -->
    <button
      type="button"
      class="
        cursor-pointer
        relative
        flex
        justify-center
        items-center
        flex-col
        w-full
        border-2 border-gray-300 border-dashed
        rounded-md
        p-5
        text-center
        hover:border-gray-400
        group
      "
    >
      <div class="flex items-center justify-center">
        <div class="flex space-x-2">
          <div class="flex items-center">
            <UploadIcon class="w-7 text-gray-300 group-hover:text-gray-400" />
          </div>
          <div class="flex items-center">
            <div class="text-left">
              <span class="text-sm font-medium text-gray-700 group-hover:text-gray-500 block">
                <span class="font-bold">
                  <span class="text-vice-teal-700 underline">Click to upload</span>
                  or drag and drop
                </span>
                <br />
                to invite multiple participants
              </span>

              <span class="mt-2 text-sm font-medium text-gray-500 group-hover:text-gray-400 block">
                CSV (max. 3MB)
              </span>
            </div>
          </div>
        </div>
      </div>
      <input
        :data-cy="`csv-${userType}-invitation`"
        type="file"
        ref="csvFileInput"
        multiple
        accept=".csv"
        @change="onCsvFileUpload"
        class="cursor-pointer opacity-0 absolute h-full w-full"
      />
    </button>

    <a
      data-cy="download-sample"
      href="/user_emails.csv"
      download="user_emails.csv"
      class="text-vice-teal-600 underline text-sm font-bold block mt-3"
      v-if="userType === 'participant'"
    >
      Download .csv sample
    </a>
    <br />
    <span class="text-red-500 text-sm break-all" v-if="error">{{ error }}</span>
  </div>
</template>

<script>
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import Papa from 'papaparse';
import axios from 'axios';
import UploadIcon from '@/Components/Icons/UploadIcon.vue';
import ViceButton from '@/Components/Buttons/ViceButton.vue';
import ViceBadge from '@/Components/Badges/ViceBadge.vue';
import userType from '@/Composables/Enums/userType';
import SelectDropdownArrow from '@/Components/Icons/SelectDropdownArrow.vue';

export default {
  name: 'UserInvitation',
  components: {
    ViceBadge,
    ViceButton,
    vSelect,
    UploadIcon,
    SelectDropdownArrow,
  },
  props: {
    userType: {
      type: String,
      required: true,
    },
    sessionType: {
      type: String,
      required: true,
    },
    maxAmount: {
      type: Number,
      required: true,
    },
    takenUsers: {
      required: true,
    },
    clinicianUniversityStudents: {
      type: Array,
      required: true,
    },
  },
  data: () => ({
    allOptions: [],
    selectedOptions: [],
    pre_selected_list: [],
    error: '',
    extractedEmailsList: [],
    searchMatchedWithSelected: false,
    highlightedInGroup: null,
  }),
  computed: {
    placeholder() {
      return window.innerWidth < 768
        ? 'Type or select emails'
        : 'Type or select email addresses to invite';
    },
    errorMessages() {
      return {
        maxExceeded: `E-mail invitations exceed the number of ${this.userType}s set for this session. Please increase the number of ${this.userType}s.`,
        alreadyAdded: 'E-mail is already added.',
      };
    },
    canAddUsers() {
      return this.selectedOptions.length < this.maxAmount;
    },
    deselect_list() {
      const allEmails = this.selectedOptions.map((user) => user.email);
      return this.pre_selected_list.filter((value) => !allEmails.includes(value));
    },
    new_selected_list() {
      const allEmails = this.selectedOptions.map((user) => user.email);
      return allEmails.filter((value) => !this.pre_selected_list.includes(value));
    },
    availableStudents() {
      // In-classroom sessions only allow facilitators to be invited as participants
      if (this.sessionType === 'in_classroom' && this.userType === 'participant') {
        return this.clinicianUniversityStudents.filter((student) => student.is_facilitator);
      }

      return this.clinicianUniversityStudents;
    },
    options() {
      // Group students by group name
      const groupsMap = {};
      const studentsWithoutGroups = [];

      this.availableStudents.forEach((student) => {
        // filter out already selected or taken users
        const isSelected = this.selectedOptions.some(
          (_selected) => _selected.email === student.email,
        );
        const isTaken = this.takenUsers?.some((email) => email === student.email);

        if (isSelected || isTaken) {
          return;
        }

        if (student.groups && student.groups.length > 0) {
          student.groups.forEach((group) => {
            if (!groupsMap[group.name]) {
              groupsMap[group.name] = {
                id: `group-${group.id}`,
                name: group.name,
                isGroup: true,
                students: [],
              };
            }
            groupsMap[group.name].students.push(student);
          });
        } else {
          studentsWithoutGroups.push(student);
        }
      });

      const options = [];

      // Add groups that have students
      Object.values(groupsMap).forEach((group) => {
        if (group.students.length > 0) {
          options.push(group);
          group.students.forEach((s) => {
            options.push({
              ...s,
              groupName: group.name,
            });
          });
        }
      });

      // Add students without groups
      studentsWithoutGroups.forEach((s) => {
        options.push(s);
      });

      return options;
    },
  },

  watch: {
    selectedOptions: {
      deep: true,
      handler() {
        const userEmails = {
          new_selected_list: this.new_selected_list,
          deselected_list: this.deselect_list,
          selected_list: this.pre_selected_list,
        };
        this.$emit('userEmailsChanged', userEmails);
      },
    },
  },
  mounted() {
    this.fetchExistingInvitations();

    fetch(route('user.emails.list'), {
      method: 'POST', // *GET, POST, PUT, DELETE, etc.
    })
      .then((response) => response.json())
      .then((result) => {
        this.allOptions = result.map((email) => ({
          email,
          email_sent: false,
        }));
      })
      .catch((error) => {
        console.error('Error:', error);
      });
  },

  methods: {
    getListLabel() {
      const labels = {
        'live_online-participant': 'Invite participants & facilitators',
        'live_online-observer': 'Invite observers',
        'in_classroom-participant': 'Invite facilitators',
        'in_classroom-observer': 'Invite observers & classroom learners',
      };

      return labels[`${this.sessionType}-${this.userType}`] ?? 'Invite participants';
    },
    fetchExistingInvitations() {
      if (!this.$page.props.session) return;

      axios.post(route('user.invite.list', this.$page.props.session.id)).then((response) => {
        const usersList = `${this.userType}_list`;

        // TODO: Convert array to object
        if (response.data[usersList].constructor.name === 'Object')
          response.data[usersList] = Object.values(response.data[usersList]);

        this.pre_selected_list = response.data[usersList]?.map((invite) => invite.email);
        this.selectedOptions = response.data[usersList];
      });
    },
    addMultipleUsers() {
      if (this.extractedEmailsList.length + this.selectedOptions.length > this.maxAmount) {
        this.error = this.errorMessages.maxExceeded;
        return;
      }
      this.extractedEmailsList.forEach((email) => {
        if (!this.takenUsers.includes(email)) {
          this.selectedOptions.push({ email, email_sent: false });
          this.$refs.usersSelect.search = '';
        } else this.error = this.errorMessages.alreadyAdded;
      });
    },
    onSearch(search, loading) {
      this.searchMatchedWithSelected = this.selectedOptions.some(({ email }) => email === search);
      this.error = '';
      this.selectedOptions = _.uniqBy(this.selectedOptions, (e) => e.email);
      if (!search.length) return;
      this.extractedEmailsList = _.uniq(
        search.split(/[\s,;]+/).filter((email) => this.validateEmail(email)),
      );
      loading(true);
      this.search(loading, search, this);
    },

    search: _.debounce((loading, search, vm) => {
      axios.post(route('user.emails.list'), { query: search }).then((response) => {
        vm.allOptions = response.data.map((email) => ({ email, email_sent: false }));
        loading(false);
      });
    }, 350),

    // eslint-disable-next-line consistent-return
    onCreateOption(email) {
      if (this.validateEmail(email)) {
        return { email, email_sent: false };
      }
      // TODO: Add all users
      // this.allOptions = [];
    },

    removeInvite(invitedUser) {
      this.selectedOptions = this.selectedOptions.filter(
        (users) => users.email != invitedUser.email,
      );
    },

    validateEmail(email) {
      return Boolean(
        email.match(
          // eslint-disable-next-line no-useless-escape
          /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        ),
      );
    },

    selectedOption(option) {
      if (option.isGroup) {
        // Add all students in the group who aren't already selected
        option.students.forEach((student) => {
          if (!this.selectedOptions.some((s) => s.email === student.email)) {
            if (this.canAddUsers) {
              this.selectedOptions.push({
                email: student.email.toLowerCase(),
                email_sent: false,
              });
            } else {
              this.error = this.errorMessages.maxExceeded;
            }
          }
        });
        this.$refs.usersSelect.clearSelection();
        return;
      }

      const value = { ...option };
      value.email = value.email.toLowerCase();
      if (this.validateEmail(value.email)) {
        const index = this.selectedOptions.findIndex(({ email }) => email === value.email);
        if (index >= 0) {
          this.selectedOptions.splice(index, 1);
        } else if (this.canAddUsers) {
          this.selectedOptions.push({
            email: value.email,
            email_sent: value.email_sent || false,
          });
        } else {
          this.error = this.errorMessages.maxExceeded;
        }
        this.$refs.usersSelect.clearSelection();
      }
    },

    onCsvFileUpload(e) {
      const self = this;
      Papa.parse(e.target.files[0], {
        delimiter: ',',
        header: true,
        skipEmptyLines: true,
        complete(results) {
          if (results.errors.length) {
            self.error = results.errors[0].message;
          } else {
            self.tidyCsvData(results.data);
          }
        },
      });
      this.$refs.csvFileInput.value = null;
    },

    tidyCsvData(csvData) {
      const invalidEmails = [];
      if (csvData.length + this.selectedOptions.length > this.maxAmount) {
        if (this.userType == userType.participant) {
          this.error =
            'E-mail invitations exceed the number of participants set for this session. Please decrease number of participants.';
        } else if (this.userType == userType.observer) {
          this.error =
            'E-mail invitations exceed the number of observers set for this session. Please decrease number of observers.';
        }
        return;
      }
      this.error = '';
      csvData.forEach((row) => {
        row.email = row.email.replace(/ /g, '').toLowerCase();
        if (this.validateEmail(row.email)) {
          if (!this.takenUsers?.some((takenEmail) => takenEmail == row.email)) {
            this.selectedOption(row);
          }
        } else {
          invalidEmails.push(row);
          this.error = `Invalid email: ${invalidEmails.map((email) => email.email)}`;
        }
      });
    },
  },
};
</script>

<style lang="scss">
::placeholder {
  color: rgb(136, 139, 136) !important;
  font-size: 14px;
}

.vs__search,
.vs__dropdown-toggle,
.vs__selected-options {
  background: none;
}

.vs__search {
  width: 100%;
  margin-top: 0.25rem;
  border-radius: 0.375rem;
  --tw-border-opacity: 1;
}

.vs__actions {
  cursor: pointer;
}

.vs__dropdown-menu {
  background: #ffffff;
  color: var(--ViCE---Dark-Grey, #606367);
  font-size: 14px;
  & .vs__dropdown-option--highlight {
    background-color: rgba(45, 212, 191, var(--tw-bg-opacity));
  }

  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 6px 0 6px 0;
  margin-top: 2px;
  position: relative;
  z-index: 9999;
  opacity: 95%;
}

.vs__dropdown-menu .vs__dropdown-option {
  padding: 0.25rem 1rem;
  border-radius: 6px;
  margin: 0 6px;
  cursor: pointer;
}

.bg-vice-teal-150 {
  background-color: #e6fcf5;
}

.vs--searchable .vs__dropdown-toggle {
  width: 100%;
  border-radius: 0.375rem;
  --tw-border-opacity: 1;
  height: 38px;
}

.vs--open .vs__dropdown-toggle {
  border-bottom-color: #d1d5db;
  font-size: 14px;
  color: rgb(136, 139, 136) !important;
}

.add-button {
  height: 38px;
  margin-top: 4px;
}
</style>
